import type { EventTypes } from 'pkg/api/models/event';
import type { Record } from 'pkg/api/models/record';

export const AIMessageRoles = {
	User: 'user',
	Assistant: 'assistant',
} as const;

export type AIMessageRole =
	(typeof AIMessageRoles)[keyof typeof AIMessageRoles];

export enum AIComponentType {
	Confirm = 'confirm',
	EventList = 'event_list',
	EventDetail = 'event_detail',
	SendChatMessage = 'send_chat_message',
	ImportCsv = 'import_csv',
	CreateGroupPost = 'create_group_post',
	Chart = 'chart',
	CreateTrainingSession = 'create_training_session',
	ExerciseOptions = 'exercise_options',
}

export enum AIChartType {
	VerticalBar = 'vertical_bar',
	HorizontalBar = 'horizontal_bar',
	Line = 'line',
	Donut = 'donut',
}

export interface AIComponentChartItem {
	label: string;
	value: number;
}

export enum AIComponentItemType {
	User = 'user',
	Event = 'event',
	Family = 'family',
}

export interface AIComponentItemUser {
	id: number;
	name: string;
	profileImageUrl: string;
	initials: string;
}

export interface AIComponentItemEvent {
	id: number;
	title: string;
	startsAt: number;
	location?: string;
}

export interface AIComponentItemFamily {
	id: number;
	name: string;
	billingContact: string;
	members: string[];
}

export type AIComponentItems =
	| AIComponentItemUser
	| AIComponentItemEvent
	| AIComponentItemFamily;

export interface AIComponent {
	id: number;
	type: AIComponentType;
	canceled: boolean;
	completed: boolean;
}

export interface AIComponentConfirm extends AIComponent {
	confirmLabel: string;
	itemType: AIComponentItemType;
	items: AIComponentItems[];
}

export interface AIComponentEventListItem {
	id: number;
	title: string;
	type: EventTypes;
	canceled: boolean;
	startsAt: number;
	endsAt: number;
	location: string;
	groupName: string;
}

export interface AIComponentEventList extends AIComponent {
	events: AIComponentEventListItem[];
}

export interface AIComponentEventDetailGame {
	homeTeamName: string;
	awayTeamName: string;
}

export interface AIComponentEventDetailItem {
	id: number;
	title: string;
	type: EventTypes;
	canceled: boolean;
	startsAt: number;
	endsAt: number;
	location: string;
	groupName: string;
	format: string;
	periods: number;
	periodLength: number;
	surface: string;
	venue: string;
	createdByName: string;
	acceptedCount: number;
	declinedCount: number;
	pendingCount: number;
	game?: AIComponentEventDetailGame;
}

export interface AIComponentEventDetail extends AIComponent {
	event: AIComponentEventDetailItem;
}

export interface AIComponentSendChatMessage extends AIComponent {
	chatId: number;
	chatName: string;
	content: string;
}

export interface AIComponentCreateGroupPost extends AIComponent {
	groupId: number;
	groupName: string;
	title: string;
	content: string;
	notify: boolean;
}

export interface AIComponentImportCSV extends AIComponent {
	confirmLabel: string;
	importType: string;
	filename: string;
	total: number;
	valid: number;
	errors: number;
	sampleErrors?: string[];
}

export interface AIComponentChart extends AIComponent {
	chartType: AIChartType;
	title?: string;
	items: AIComponentChartItem[];
}

export interface AIComponentTrainingSessionExercise {
	id: number;
	title: string;
	minTime: number;
	maxTime: number;
}

export interface AIComponentTrainingSessionBlock {
	title: string;
	description?: string;
	duration: number;
	exercises: AIComponentTrainingSessionExercise[];
}

export interface AIComponentCreateTrainingSession extends AIComponent {
	title: string;
	description?: string;
	totalDuration: number;
	blocks: AIComponentTrainingSessionBlock[];
	// In-app path of the created session, present once the card is confirmed.
	sessionPath?: string;
	// Event the session is added to on confirm; absent for library sessions.
	eventId?: number;
	// Group library the session is saved to on confirm; absent for personal
	// library and event sessions.
	groupId?: number;
	groupName?: string;
}

export interface AIComponentExerciseOption {
	id: number;
	title: string;
	minTime: number;
	maxTime: number;
	minParticipants: number;
	maxParticipants: number;
	minAge: number;
	maxAge: number;
	description?: string;
}

export interface AIComponentExerciseRef {
	id: number;
	title: string;
}

export interface AIComponentExerciseOptions extends AIComponent {
	exercises: AIComponentExerciseOption[];
	// The staged exercise the options are offered in place of.
	replaces?: AIComponentExerciseRef;
}

export type AIComponents =
	| AIComponentConfirm
	| AIComponentEventList
	| AIComponentEventDetail
	| AIComponentSendChatMessage
	| AIComponentCreateGroupPost
	| AIComponentImportCSV
	| AIComponentChart
	| AIComponentCreateTrainingSession
	| AIComponentExerciseOptions;

export interface AIMessageAttachment {
	attachmentId: number;
	filename: string;
}

export interface AIMessage extends Record {
	conversationId: number;
	role: AIMessageRole;
	content: string;
	createdAt: number;
	components?: AIComponents[];
	attachments?: AIMessageAttachment[];
	userRating?: boolean | null;
	// Client-only: set when an optimistic message could not be processed
	// (e.g. rate-limited). Rendered as a caption under the bubble.
	error?: string;
}
